/**
 * ExpenseTracker.jsx — v2
 * Uses useTheme() from ThemeContext — no module-level T references.
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  DollarSign, Plus, Search, Edit2, Trash2, Download,
  TrendingUp, TrendingDown, Package, Cloud, Monitor,
  Briefcase, Globe, Users, Scale, X, BarChart2,
  RefreshCw, AlertCircle, FileText, Repeat, Target,
  Building2, Bell, CheckCircle, Receipt,
} from "lucide-react";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";
import ReimbursementsTab from "./ReimbursementsTab";

const API = apiBaseUrl;

// ── Category config ────────────────────────────────────────────────
const CATEGORIES = [
  { key:"saas",          label:"SaaS & Software",          icon:Monitor    },
  { key:"infrastructure",label:"Cloud & Infrastructure",    icon:Cloud      },
  { key:"office",        label:"Office & Operations",       icon:Briefcase  },
  { key:"marketing",     label:"Marketing & Ads",           icon:Globe      },
  { key:"travel",        label:"Travel & Transport",        icon:Package    },
  { key:"hr",            label:"HR & Recruitment",          icon:Users      },
  { key:"legal",         label:"Legal & Compliance",        icon:Scale      },
  { key:"reimbursement", label:"Employee Reimbursements",   icon:Receipt    },
  { key:"other",         label:"Other",                     icon:AlertCircle},
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

// Category colors resolved at render time from T
const CAT_COLOR_KEY = {
  saas:"cyan", infrastructure:"purple", office:"green",
  marketing:"amber", travel:"amber", hr:"red", legal:"cyan",
  reimbursement:"purple", other:"dim",
};

const QUICK_VENDORS = [
  {name:"Jira",      cat:"saas",           icon:"🔵"},
  {name:"GitHub",    cat:"saas",           icon:"⬛"},
  {name:"Slack",     cat:"saas",           icon:"💬"},
  {name:"Zoom",      cat:"saas",           icon:"📹"},
  {name:"Notion",    cat:"saas",           icon:"📝"},
  {name:"Figma",     cat:"saas",           icon:"🎨"},
  {name:"AWS",       cat:"infrastructure", icon:"🟠"},
  {name:"Azure",     cat:"infrastructure", icon:"🔷"},
  {name:"GCP",       cat:"infrastructure", icon:"🔴"},
  {name:"Hostinger", cat:"infrastructure", icon:"🌐"},
  {name:"Postman",   cat:"saas",           icon:"📬"},
  {name:"HubSpot",   cat:"marketing",      icon:"🟠"},
];

const fmtINR  = n => `₹${Number(n||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`;
const fmtINR2 = n => `₹${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const curMonth  = () => new Date().toISOString().slice(0,7);
const monthLbl  = m => { if(!m) return ""; const [y,mo]=m.split("-"); return new Date(y,mo-1).toLocaleDateString("en-IN",{month:"short",year:"numeric"}); };
const prevMonth = m => { const y=+m.slice(0,4),mo=+m.slice(5); return mo>1?`${y}-${String(mo-1).padStart(2,"0")}`:`${y-1}-12`; };

// ── Shared UI ──────────────────────────────────────────────────────
function Card({children, style={}}) {
  const {T} = useTheme();
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,...style}}>
      {children}
    </div>
  );
}

function Btn({onClick,disabled,variant="primary",size="md",children,style={}}) {
  const {T} = useTheme();
  const bg    = variant==="primary" ? `linear-gradient(135deg,${T.cyan},${T.purple})`
              : variant==="danger"  ? `rgba(255,77,109,0.12)` : T.toggle_bg;
  const color = variant==="primary" ? "#fff" : variant==="danger" ? T.red : T.muted;
  const border= variant==="primary" ? "none" : `1px solid ${color}30`;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:size==="sm"?"5px 12px":"9px 20px", borderRadius:8, border,
      background:bg, color, cursor:disabled?"not-allowed":"pointer",
      opacity:disabled?0.5:1, fontFamily:"Outfit,sans-serif",
      fontSize:size==="sm"?12:13, fontWeight:700,
      display:"inline-flex", alignItems:"center", gap:6, ...style,
    }}>{children}</button>
  );
}

function Bar({pct=0, color, height=6}) {
  const {T} = useTheme();
  return (
    <div style={{height, background:T.toggle_bg, borderRadius:height/2}}>
      <div style={{height:"100%", borderRadius:height/2,
        width:`${Math.min(100,Math.max(0,pct))}%`,
        background:color, transition:"width 0.6s ease"}}/>
    </div>
  );
}

function StatCard({label,value,sub,colorKey="cyan",icon,mom,alert}) {
  const {T} = useTheme();
  const color = T[colorKey] || T.cyan;
  return (
    <Card style={{padding:"18px 20px",flex:1,minWidth:150,
      border:alert?`1px solid ${T.red}30`:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontSize:10,color:T.muted,fontWeight:700,
          textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</div>
        {icon && React.createElement(icon,{size:14,style:{color,opacity:0.7}})}
      </div>
      <div style={{fontSize:22,fontWeight:900,color,
        fontFamily:"JetBrains Mono,monospace"}}>{value}</div>
      {sub && <div style={{fontSize:11,color:T.dim,marginTop:3}}>{sub}</div>}
      {mom!=null && (
        <div style={{fontSize:11,marginTop:4,display:"flex",alignItems:"center",gap:4,
          color:mom<=0?T.green:T.red}}>
          {mom<=0?<TrendingDown size={11}/>:<TrendingUp size={11}/>}
          {Math.abs(mom)}% vs last month
        </div>
      )}
    </Card>
  );
}

function Toast({msg}) {
  const {T} = useTheme();
  if(!msg) return null;
  const ok = msg.startsWith("✅");
  return (
    <div style={{position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",
      padding:"10px 22px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,
      background:T.card,
      border:`1px solid ${ok?"rgba(0,229,163,0.3)":"rgba(255,77,109,0.3)"}`,
      color:ok?T.green:T.red,
      boxShadow:"0 8px 32px rgba(0,0,0,0.4)",fontFamily:"Outfit,sans-serif"}}>
      {msg}
    </div>
  );
}

function useInp() {
  const {T} = useTheme();
  return {
    background:T.inp_bg, border:`1px solid ${T.border}`,
    borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
    outline:"none", fontFamily:"Outfit,sans-serif",
    boxSizing:"border-box", width:"100%",
  };
}
function useLbl() {
  const {T} = useTheme();
  return {fontSize:11,color:T.muted,display:"block",marginBottom:5,
    textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700};
}

// ══════════════════════════════════════════════════════════════════
// EXPENSE FORM MODAL
// ══════════════════════════════════════════════════════════════════
function ExpenseModal({expense,onClose,onSaved,flash}) {
  const {T,theme} = useTheme();
  const inp = useInp();
  const lbl = useLbl();
  const today = new Date().toISOString().slice(0,10);
  const [form,setForm] = useState({
    category:expense?.category||"saas", vendor_name:expense?.vendor_name||"",
    description:expense?.description||"", amount:expense?.amount||"",
    currency:expense?.currency||"INR", frequency:expense?.frequency||"monthly",
    expense_date:expense?.expense_date||today, tags:expense?.tags||"",
    is_recurring:expense?.is_recurring||false,
  });
  const [saving,setSaving] = useState(false);
  const f = k => e => setForm(p=>({...p,[k]:e.target.value}));

  const submit = async() => {
    if(!form.vendor_name||!form.amount){flash("❌ Vendor and amount required");return;}
    setSaving(true);
    try {
      if(expense?.id) await axios.put(`${API}/expenses/${expense.id}`,form);
      else            await axios.post(`${API}/expenses/`,form);
      flash(`✅ Expense ${expense?.id?"updated":"added"}`);
      onSaved(); onClose();
    } catch(e){flash("❌ "+(e.response?.data?.detail||"Error"));}
    finally{setSaving(false);}
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9998,
      display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,
        borderRadius:14,width:520,overflow:"hidden",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{padding:"18px 24px",background:T.card2,
          borderBottom:`1px solid ${T.border}`,
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:700,color:T.text}}>
            {expense?.id?"Edit Expense":"Add Expense"}
          </h3>
          <button onClick={onClose} style={{background:"none",border:"none",
            cursor:"pointer",color:T.muted}}><X size={16}/></button>
        </div>
        <div style={{padding:24,display:"flex",flexDirection:"column",gap:14}}>
          {!expense?.id&&(
            <div>
              <label style={lbl}>Quick Add</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {QUICK_VENDORS.map(v=>(
                  <button key={v.name}
                    onClick={()=>setForm(p=>({...p,vendor_name:v.name,category:v.cat}))}
                    style={{padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:600,
                      cursor:"pointer",fontFamily:"Outfit,sans-serif",
                      border:`1px solid ${form.vendor_name===v.name?T.cyan:T.border}`,
                      background:form.vendor_name===v.name?`${T.cyan}18`:"transparent",
                      color:form.vendor_name===v.name?T.cyan:T.muted}}>
                    {v.icon} {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label style={lbl}>Category *</label>
              <select value={form.category} onChange={f("category")} style={{...inp,cursor:"pointer"}}>
                {CATEGORIES.map(c=><option key={c.key} value={c.key} style={{background:T.opt_bg}}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Vendor *</label>
              <input value={form.vendor_name} onChange={f("vendor_name")} placeholder="e.g. Jira, AWS" style={inp}/>
            </div>
          </div>
          <div>
            <label style={lbl}>Description</label>
            <input value={form.description} onChange={f("description")} placeholder="Team license" style={inp}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 90px 130px",gap:12}}>
            <div>
              <label style={lbl}>Amount *</label>
              <input value={form.amount} onChange={f("amount")} type="number" min="0" step="0.01" placeholder="0.00" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Currency</label>
              <select value={form.currency} onChange={f("currency")} style={{...inp,cursor:"pointer"}}>
                {["INR","USD","EUR","GBP"].map(c=><option key={c} style={{background:T.opt_bg}}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Frequency</label>
              <select value={form.frequency} onChange={f("frequency")} style={{...inp,cursor:"pointer"}}>
                {["monthly","quarterly","annual","one-time"].map(v=>(
                  <option key={v} style={{background:T.opt_bg}}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <label style={lbl}>Date</label>
              <input type="date" value={form.expense_date} onChange={f("expense_date")} style={{...inp,colorScheme:theme}}/>
            </div>
            <div>
              <label style={lbl}>Tags</label>
              <input value={form.tags} onChange={f("tags")} placeholder="dev-tools, q2" style={inp}/>
            </div>
          </div>
          <div style={{padding:"12px 14px",borderRadius:8,cursor:"pointer",
            background:T.card2,border:`1px solid ${form.is_recurring?T.cyan:T.border}`,
            display:"flex",alignItems:"center",justifyContent:"space-between"}}
            onClick={()=>setForm(p=>({...p,is_recurring:!p.is_recurring}))}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <Repeat size={15} style={{color:form.is_recurring?T.cyan:T.dim}}/>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:form.is_recurring?T.cyan:T.text}}>
                  Recurring Expense
                </div>
                <div style={{fontSize:11,color:T.dim}}>Auto-suggest every month</div>
              </div>
            </div>
            <div style={{width:36,height:20,borderRadius:10,position:"relative",
              background:form.is_recurring?T.cyan:T.toggle_bg,transition:"background 0.2s"}}>
              <div style={{position:"absolute",top:2,left:form.is_recurring?18:2,
                width:16,height:16,borderRadius:"50%",background:"white",transition:"left 0.2s"}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",
            paddingTop:8,borderTop:`1px solid ${T.border}`}}>
            <Btn onClick={onClose} variant="ghost">Cancel</Btn>
            <Btn onClick={submit} disabled={saving}>
              {saving?"Saving…":<><Plus size={13}/>{expense?.id?"Update":"Add"}</>}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ══════════════════════════════════════════════════════════════════
function DashboardTab({month,summary,loading}) {
  const {T} = useTheme();
  if(loading) return <div style={{padding:60,textAlign:"center",color:T.muted}}>Loading…</div>;
  if(!summary) return null;
  const maxCat = Math.max(...(summary.by_category||[]).map(c=>c.amount),1);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {summary.budget_alerts?.length>0&&(
        <div style={{padding:"12px 16px",borderRadius:10,
          background:`${T.red}10`,border:`1px solid ${T.red}30`,
          display:"flex",flexWrap:"wrap",gap:8,alignItems:"center"}}>
          <Bell size={14} style={{color:T.red}}/>
          <span style={{fontSize:12,fontWeight:700,color:T.red}}>Budget Alerts:</span>
          {summary.budget_alerts.map((a,i)=>(
            <span key={i} style={{fontSize:12,padding:"2px 8px",borderRadius:20,
              background:`${T.red}18`,color:T.red}}>{a.label} — {a.pct}%</span>
          ))}
        </div>
      )}
      {summary.pending_recurring>0&&(
        <div style={{padding:"10px 16px",borderRadius:10,
          background:`${T.cyan}08`,border:`1px solid ${T.cyan}20`,
          display:"flex",alignItems:"center",gap:8,fontSize:12}}>
          <Repeat size={13} style={{color:T.cyan}}/>
          <span style={{color:T.muted}}>
            {summary.pending_recurring} recurring expense(s) from last month not yet logged.
          </span>
        </div>
      )}
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <StatCard label="Total Spend"  value={fmtINR(summary.grand_total)}
          colorKey="cyan" icon={DollarSign} mom={summary.mom_pct} sub={monthLbl(month)}/>
        <StatCard label="Vendor & Ops" value={fmtINR(summary.vendor_total)}
          colorKey="purple" icon={Package}
          sub={`${(summary.by_category||[]).length} categories`}/>
        <StatCard label="Salary Cost"  value={fmtINR(summary.salary_gross)}
          colorKey="green" icon={Users} sub={`Net: ${fmtINR(summary.salary_net)}`}/>
        <StatCard label="Salary %"
          value={summary.grand_total>0?`${Math.round(summary.salary_gross/summary.grand_total*100)}%`:"—"}
          colorKey="amber" icon={BarChart2} sub="of total spend"/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:16}}>
        <Card style={{padding:"20px 22px"}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:16}}>
            Category Breakdown
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {summary.salary_gross>0&&(
              <div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <Users size={13} style={{color:T.green}}/>
                    <span style={{fontSize:13,color:T.muted}}>Salary & Benefits</span>
                    <span style={{fontSize:9,padding:"1px 6px",borderRadius:10,
                      background:`${T.green}18`,color:T.green,fontWeight:700}}>PAYROLL</span>
                  </div>
                  <span style={{fontSize:13,fontWeight:700,color:T.green,
                    fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(summary.salary_gross)}</span>
                </div>
                <Bar pct={summary.grand_total>0?summary.salary_gross/summary.grand_total*100:0} color={T.green} height={8}/>
              </div>
            )}
            {(summary.by_category||[]).map(cat=>{
              const color = T[CAT_COLOR_KEY[cat.category]]||T.cyan;
              // Reimbursements get a special badge
              const isReimb = cat.category==="reimbursement";
              return (
                <div key={cat.category}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:8,height:8,borderRadius:2,background:color}}/>
                      <span style={{fontSize:13,color:T.muted}}>{cat.label}</span>
                      {isReimb&&(
                        <span style={{fontSize:9,padding:"1px 6px",borderRadius:10,
                          background:`${T.purple}18`,color:T.purple,fontWeight:700}}>
                          AUTO
                        </span>
                      )}
                      {cat.over_budget&&<span style={{fontSize:9,padding:"1px 6px",
                        borderRadius:10,background:`${T.red}18`,color:T.red,fontWeight:700}}>OVER</span>}
                    </div>
                    <div>
                      <span style={{fontSize:13,fontWeight:700,color,
                        fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(cat.amount)}</span>
                      <span style={{fontSize:10,color:T.dim,marginLeft:6}}>{cat.pct}%</span>
                    </div>
                  </div>
                  <Bar pct={cat.amount/maxCat*100} color={color} height={8}/>
                </div>
              );
            })}
            {(summary.by_category||[]).length===0&&summary.salary_gross===0&&(
              <div style={{padding:"32px 0",textAlign:"center",color:T.dim,fontSize:13}}>
                No expenses for {monthLbl(month)}
              </div>
            )}
          </div>
        </Card>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Card style={{padding:"18px",flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Top Vendors</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {(summary.top_vendors||[]).map((v,i)=>{
                const color = T[CAT_COLOR_KEY[v.category]]||T.cyan;
                return (
                  <div key={i} style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",padding:"8px 10px",borderRadius:8,background:T.card2}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:T.text}}>{v.vendor}</div>
                      <div style={{fontSize:10,color:T.dim}}>{CAT_MAP[v.category]?.label}</div>
                    </div>
                    <div style={{fontSize:14,fontWeight:800,color,
                      fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(v.amount)}</div>
                  </div>
                );
              })}
              {!(summary.top_vendors?.length>0)&&(
                <div style={{textAlign:"center",color:T.dim,fontSize:13,padding:"16px 0"}}>No vendors yet</div>
              )}
            </div>
          </Card>
          {summary.grand_total>0&&(
            <Card style={{padding:"16px",background:`linear-gradient(135deg,${T.cyan}08,${T.purple}08)`}}>
              <div style={{fontSize:10,color:T.muted,textTransform:"uppercase",
                letterSpacing:"0.08em",marginBottom:4}}>Total Cost</div>
              <div style={{fontSize:26,fontWeight:900,color:T.cyan,
                fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(summary.grand_total)}</div>
              {summary.mom_pct!==0&&(
                <div style={{fontSize:11,marginTop:4,
                  color:summary.mom_pct<=0?T.green:T.red,
                  display:"flex",alignItems:"center",gap:4}}>
                  {summary.mom_pct<=0?<TrendingDown size={10}/>:<TrendingUp size={10}/>}
                  {Math.abs(summary.mom_pct)}% vs {monthLbl(summary.prev_month)}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ANALYTICS TAB
// ══════════════════════════════════════════════════════════════════
function AnalyticsTab({flash}) {
  const {T} = useTheme();
  const [data,setData]       = useState(null);
  const [loading,setLoading] = useState(true);
  useEffect(()=>{
    setLoading(true);
    axios.get(`${API}/expenses/analytics?months=6`)
      .then(r=>setData(r.data)).catch(()=>flash("❌ Failed to load analytics"))
      .finally(()=>setLoading(false));
  },[]);
  if(loading) return <div style={{padding:60,textAlign:"center",color:T.muted}}>Loading…</div>;
  if(!data)   return null;
  const maxGrand = Math.max(...(data.trend||[]).map(t=>t.grand),1);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <StatCard label="Avg Monthly" value={fmtINR(data.totals?.avg_monthly)} colorKey="cyan" icon={BarChart2} sub="Last 6 months"/>
        <StatCard label="Next Month Forecast" value={fmtINR(data.forecast_next)} colorKey="purple" icon={TrendingUp} sub="3-month avg"/>
        <StatCard label="6-Month Total" value={fmtINR(data.totals?.grand)} colorKey="green" icon={DollarSign}/>
      </div>
      <Card style={{padding:"22px"}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:20}}>
          Monthly Spend — Last 6 Months
        </div>
        <div style={{display:"flex",gap:8,alignItems:"flex-end",height:160}}>
          {(data.trend||[]).map((t,i)=>{
            const vendorH = t.vendor/maxGrand*130;
            const salH    = t.salary/maxGrand*130;
            return (
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:10,color:T.dim,fontFamily:"JetBrains Mono,monospace"}}>
                  {fmtINR(t.grand)}
                </div>
                <div style={{display:"flex",gap:2,alignItems:"flex-end",width:"100%"}}>
                  <div style={{flex:1,height:Math.max(2,vendorH),background:T.purple,
                    borderRadius:"3px 3px 0 0",transition:"height 0.5s"}}/>
                  <div style={{flex:1,height:Math.max(2,salH),background:T.green,
                    borderRadius:"3px 3px 0 0",transition:"height 0.5s"}}/>
                </div>
                {t.mom_pct!=null&&(
                  <div style={{fontSize:9,color:t.mom_pct<=0?T.green:T.red}}>
                    {t.mom_pct>0?"+":""}{t.mom_pct}%
                  </div>
                )}
                <div style={{fontSize:10,color:T.muted}}>{monthLbl(t.month)}</div>
              </div>
            );
          })}
          <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{fontSize:10,color:T.purple,fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(data.forecast_next)}</div>
            <div style={{width:"100%",height:Math.max(2,data.forecast_next/maxGrand*130),
              border:`2px dashed ${T.purple}`,borderRadius:"3px 3px 0 0",minHeight:2}}/>
            <div style={{fontSize:10,color:T.purple,fontWeight:700}}>Forecast</div>
          </div>
        </div>
        <div style={{display:"flex",gap:16,marginTop:12,justifyContent:"center"}}>
          {[["Vendor",T.purple],["Salary",T.green]].map(([l,c])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.muted}}>
              <div style={{width:10,height:10,borderRadius:2,background:c}}/>{l}
            </div>
          ))}
        </div>
      </Card>
      {(data.category_growth||[]).length>0&&(
        <Card style={{padding:"22px"}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:16}}>
            Category Trends — Last Month vs Previous
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {data.category_growth.map((c,i)=>{
              const color = T[CAT_COLOR_KEY[c.category]]||T.cyan;
              const maxV  = Math.max(...data.category_growth.map(x=>x.current),1);
              return (
                <div key={i} style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:8,height:8,borderRadius:2,background:color,flexShrink:0}}/>
                  <div style={{width:130,fontSize:12,color:T.muted,flexShrink:0}}>{c.label}</div>
                  <div style={{flex:1}}><Bar pct={c.current/maxV*100} color={color} height={6}/></div>
                  <div style={{width:80,fontFamily:"JetBrains Mono,monospace",fontSize:12,
                    fontWeight:700,color,textAlign:"right"}}>{fmtINR(c.current)}</div>
                  {c.change_pct!=null&&(
                    <div style={{width:55,fontSize:11,textAlign:"right",
                      color:c.growing?T.red:T.green,display:"flex",alignItems:"center",
                      justifyContent:"flex-end",gap:3}}>
                      {c.growing?<TrendingUp size={10}/>:<TrendingDown size={10}/>}
                      {Math.abs(c.change_pct)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// BUDGET TAB
// ══════════════════════════════════════════════════════════════════
function BudgetTab({month,flash}) {
  const {T} = useTheme();
  const inp = useInp(); const lbl = useLbl();
  const [data,setData]      = useState(null);
  const [loading,setLoading]= useState(true);
  const [editing,setEditing]= useState({});
  const [saving,setSaving]  = useState(null);

  const load = useCallback(async()=>{
    setLoading(true);
    try { const r=await axios.get(`${API}/expenses/budgets?month=${month}`); setData(r.data); }
    catch{flash("❌ Failed");}
    finally{setLoading(false);}
  },[month]);
  useEffect(()=>{load();},[load]);

  const save = async(cat,val)=>{
    if(!val||isNaN(val)){flash("❌ Enter valid amount");return;}
    setSaving(cat);
    try{
      await axios.put(`${API}/expenses/budgets`,{category:cat,month,budget_amount:parseFloat(val)});
      flash("✅ Budget saved"); setEditing(p=>({...p,[cat]:""})); load();
    }catch{flash("❌ Save failed");}
    finally{setSaving(null);}
  };
  const copyPrev=async()=>{
    const prev=prevMonth(month);
    try{const r=await axios.post(`${API}/expenses/budgets/copy?from_month=${prev}&to_month=${month}`);flash(`✅ ${r.data.message}`);load();}
    catch{flash("❌ Copy failed");}
  };

  if(loading) return <div style={{padding:60,textAlign:"center",color:T.muted}}>Loading…</div>;
  if(!data)   return null;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
        <StatCard label="Total Budget"   value={fmtINR(data.total_budget)}   colorKey="cyan"   icon={Target}/>
        <StatCard label="Total Spent"    value={fmtINR(data.total_actual)}   colorKey="purple" icon={DollarSign}
          alert={data.total_actual>data.total_budget&&data.total_budget>0}/>
        <StatCard label="Remaining"      value={fmtINR(data.total_remaining)}
          colorKey={data.total_remaining<0?"red":"green"} icon={CheckCircle}/>
        <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}>
          <Btn onClick={copyPrev} variant="ghost" size="sm"><RefreshCw size={12}/> Copy last month</Btn>
        </div>
      </div>
      {data.alert_count>0&&(
        <div style={{padding:"10px 16px",borderRadius:8,background:`${T.red}10`,
          border:`1px solid ${T.red}30`,fontSize:12,color:T.red,
          display:"flex",alignItems:"center",gap:8}}>
          <Bell size={13}/>
          {data.alert_count} categor{data.alert_count>1?"ies":"y"} at 80%+ of budget.
        </div>
      )}
      <Card>
        <div style={{padding:"12px 20px",borderBottom:`1px solid ${T.border}`,
          display:"grid",gridTemplateColumns:"160px 1fr 110px 110px 80px 130px",gap:12,
          fontSize:10,color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>
          <span>Category</span><span>Progress</span>
          <span>Budget</span><span>Spent</span><span>Used</span><span>Set Budget</span>
        </div>
        {(data.categories||[]).map((c,i)=>{
          const catObj = CAT_MAP[c.category];
          const color  = T[CAT_COLOR_KEY[c.category]]||T.cyan;
          const barC   = c.over_budget?T.red:c.pct_used>=80?T.amber:color;
          return (
            <div key={c.category} style={{padding:"13px 20px",
              borderBottom:i<data.categories.length-1?`1px solid ${T.border}30`:"none",
              display:"grid",gridTemplateColumns:"160px 1fr 110px 110px 80px 130px",
              gap:12,alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {catObj&&React.createElement(catObj.icon,{size:13,style:{color}})}
                <span style={{fontSize:12,color:T.text,fontWeight:600}}>{c.label}</span>
              </div>
              <div><Bar pct={c.budget_amount>0?(c.pct_used||0):0} color={barC} height={8}/></div>
              <div style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,color:T.muted}}>
                {c.budget_amount>0?fmtINR(c.budget_amount):"—"}
              </div>
              <div style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,fontWeight:700,
                color:c.over_budget?T.red:T.text}}>{fmtINR(c.actual_amount)}</div>
              <div style={{fontSize:12,fontWeight:700,
                color:c.over_budget?T.red:c.pct_used>=80?T.amber:T.muted}}>
                {c.pct_used!=null?`${c.pct_used}%`:"—"}
              </div>
              <div style={{display:"flex",gap:6}}>
                <input type="number" min="0" placeholder="₹0"
                  value={(editing[c.category]??c.budget_amount)||""}
                  onChange={e=>setEditing(p=>({...p,[c.category]:e.target.value}))}
                  style={{...inp,padding:"5px 8px",fontSize:12,width:70}}/>
                <Btn size="sm" disabled={saving===c.category}
                  onClick={()=>save(c.category,(editing[c.category]??c.budget_amount))}>
                  {saving===c.category?"…":"Set"}
                </Btn>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// VENDORS TAB
// ══════════════════════════════════════════════════════════════════
function VendorsTab({month,onRefresh,flash}) {
  const {T} = useTheme();
  const inp = useInp();
  const [expenses,setExpenses]   = useState([]);
  const [loading,setLoading]     = useState(true);
  const [search,setSearch]       = useState("");
  const [catFilter,setCatFilter] = useState("");
  const [recurOnly,setRecurOnly] = useState(false);
  const [showModal,setShowModal] = useState(false);
  const [editItem,setEditItem]   = useState(null);
  const [preview,setPreview]     = useState(null);

  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const p=new URLSearchParams();
      if(month)     p.append("month",month);
      if(catFilter) p.append("category",catFilter);
      if(search)    p.append("search",search);
      if(recurOnly) p.append("is_recurring","true");
      const r=await axios.get(`${API}/expenses/?${p}`);
      setExpenses(r.data||[]);
    }catch{flash("❌ Failed to load");}
    finally{setLoading(false);}
  },[month,catFilter,search,recurOnly]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{
    if(!month) return;
    axios.get(`${API}/expenses/recurring/preview?month=${month}`)
      .then(r=>setPreview(r.data)).catch(()=>{});
  },[month]);

  const del=async id=>{
    if(!window.confirm("Delete?")) return;
    try{await axios.delete(`${API}/expenses/${id}`);flash("✅ Deleted");load();onRefresh();}
    catch{flash("❌ Failed");}
  };
  const autoLog=async()=>{
    try{const r=await axios.post(`${API}/expenses/recurring/auto-log?month=${month}`);
      flash(`✅ ${r.data.message}`);load();onRefresh();}
    catch{flash("❌ Failed");}
  };
  const total=expenses.reduce((a,e)=>a+e.amount,0);

  return (
    <div>
      {preview?.total_pending>0&&(
        <div style={{marginBottom:14,padding:"12px 16px",borderRadius:10,
          background:`${T.cyan}08`,border:`1px solid ${T.cyan}20`,
          display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:T.muted}}>
            <Repeat size={14} style={{color:T.cyan}}/>
            <span><strong style={{color:T.cyan}}>{preview.total_pending}</strong> recurring expense(s) from {monthLbl(preview.prev_month)} not logged yet.</span>
          </div>
          <Btn size="sm" onClick={autoLog}><Repeat size={11}/>Auto-Log All</Btn>
        </div>
      )}
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{position:"relative",flex:1,minWidth:200}}>
          <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.dim}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search vendors…" style={{...inp,paddingLeft:32}}/>
        </div>
        <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
          style={{...inp,width:"auto",cursor:"pointer"}}>
          <option value="" style={{background:T.opt_bg}}>All Categories</option>
          {CATEGORIES.map(c=><option key={c.key} value={c.key} style={{background:T.opt_bg}}>{c.label}</option>)}
        </select>
        <button onClick={()=>setRecurOnly(!recurOnly)} style={{
          padding:"9px 14px",borderRadius:8,cursor:"pointer",fontFamily:"Outfit,sans-serif",
          fontSize:12,fontWeight:700,border:`1px solid ${recurOnly?T.cyan:T.border}`,
          background:recurOnly?`${T.cyan}18`:"transparent",
          color:recurOnly?T.cyan:T.muted,display:"flex",alignItems:"center",gap:6}}>
          <Repeat size={12}/> Recurring
        </button>
        <Btn onClick={()=>{setEditItem(null);setShowModal(true);}}>
          <Plus size={13}/>Add Expense
        </Btn>
      </div>
      <Card>
        {expenses.length>0&&(
          <div style={{padding:"10px 20px",borderBottom:`1px solid ${T.border}`,
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,color:T.muted}}>{expenses.length} expense(s)</span>
            <span style={{fontSize:14,fontWeight:800,color:T.cyan,
              fontFamily:"JetBrains Mono,monospace"}}>{fmtINR2(total)}</span>
          </div>
        )}
        {loading?(
          <div style={{padding:40,textAlign:"center",color:T.muted}}>Loading…</div>
        ):expenses.length===0?(
          <div style={{padding:"48px 24px",textAlign:"center"}}>
            <DollarSign size={36} style={{color:T.dim,marginBottom:12}}/>
            <div style={{fontSize:14,fontWeight:600,color:T.text,marginBottom:12}}>No expenses</div>
            <Btn onClick={()=>setShowModal(true)}><Plus size={13}/>Add First Expense</Btn>
          </div>
        ):(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${T.border}`}}>
                  {["Vendor","Category","Amount","Frequency","Date",""].map(h=>(
                    <th key={h} style={{padding:"10px 16px",textAlign:"left",fontSize:10,
                      color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenses.map((e,i)=>{
                  const color=T[CAT_COLOR_KEY[e.category]]||T.cyan;
                  return (
                    <tr key={e.id} style={{borderBottom:`1px solid ${T.border}30`,
                      background:i%2===0?"transparent":T.card2}}>
                      <td style={{padding:"11px 16px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontWeight:600,color:T.text}}>{e.vendor_name}</span>
                          {e.is_recurring&&<Repeat size={11} style={{color:T.cyan}}/>}
                        </div>
                        {e.description&&<div style={{fontSize:11,color:T.dim,marginTop:2}}>{e.description}</div>}
                      </td>
                      <td style={{padding:"11px 16px"}}>
                        <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,fontWeight:700,
                          background:`${color}18`,color}}>{e.category_label}</span>
                      </td>
                      <td style={{padding:"11px 16px",fontFamily:"JetBrains Mono,monospace",
                        fontWeight:700,color:T.cyan}}>{fmtINR(e.amount)}</td>
                      <td style={{padding:"11px 16px"}}>
                        <span style={{padding:"2px 8px",borderRadius:20,fontSize:10,
                          fontWeight:600,background:T.card2,color:T.muted}}>{e.frequency}</span>
                      </td>
                      <td style={{padding:"11px 16px",color:T.dim,fontSize:12}}>{e.expense_date}</td>
                      <td style={{padding:"11px 16px"}}>
                        <div style={{display:"flex",gap:6}}>
                          <Btn size="sm" variant="ghost" onClick={()=>{setEditItem(e);setShowModal(true);}}><Edit2 size={11}/></Btn>
                          <Btn size="sm" variant="danger" onClick={()=>del(e.id)}><Trash2 size={11}/></Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {showModal&&(
        <ExpenseModal expense={editItem}
          onClose={()=>{setShowModal(false);setEditItem(null);}}
          onSaved={()=>{load();onRefresh();}} flash={flash}/>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// DEPARTMENT TAB
// ══════════════════════════════════════════════════════════════════
function DepartmentTab({month,flash}) {
  const {T} = useTheme();
  const [data,setData]       = useState(null);
  const [loading,setLoading] = useState(true);
  useEffect(()=>{
    if(!month) return;
    setLoading(true);
    axios.get(`${API}/expenses/department-breakdown?month=${month}`)
      .then(r=>setData(r.data)).catch(()=>flash("❌ Failed"))
      .finally(()=>setLoading(false));
  },[month]);
  if(loading) return <div style={{padding:60,textAlign:"center",color:T.muted}}>Loading…</div>;
  if(!data)   return null;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:12}}>
        <StatCard label="Total Cost"  value={fmtINR(data.grand_total)} colorKey="cyan"   icon={Building2}/>
        <StatCard label="Departments" value={(data.departments||[]).length} colorKey="purple" icon={Users}/>
      </div>
      <Card>
        <div style={{padding:"12px 20px",borderBottom:`1px solid ${T.border}`,
          display:"grid",gridTemplateColumns:"160px 1fr 110px 110px 70px 110px",gap:12,
          fontSize:10,color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>
          <span>Department</span><span>Breakdown</span>
          <span>Vendor</span><span>Salary</span><span>Head</span><span>Total</span>
        </div>
        {(data.departments||[]).length===0?(
          <div style={{padding:"48px 24px",textAlign:"center",color:T.dim,fontSize:13}}>
            No department data. Assign department_id to expenses.
          </div>
        ):data.departments.map((d,i)=>(
          <div key={d.department_id} style={{padding:"13px 20px",
            borderBottom:i<data.departments.length-1?`1px solid ${T.border}30`:"none",
            display:"grid",gridTemplateColumns:"160px 1fr 110px 110px 70px 110px",
            gap:12,alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:T.text}}>{d.department_name}</div>
              <div style={{fontSize:10,color:T.dim}}>{d.pct_of_total}% of total</div>
            </div>
            <div style={{height:8,borderRadius:4,overflow:"hidden",background:T.toggle_bg,display:"flex"}}>
              <div style={{height:"100%",width:`${d.total_cost>0?d.vendor_spend/d.total_cost*100:0}%`,background:T.purple}}/>
              <div style={{height:"100%",width:`${d.total_cost>0?d.salary_cost/d.total_cost*100:0}%`,background:T.green}}/>
            </div>
            <div style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,color:T.purple}}>{fmtINR(d.vendor_spend)}</div>
            <div style={{fontFamily:"JetBrains Mono,monospace",fontSize:12,color:T.green}}>{fmtINR(d.salary_cost)}</div>
            <div style={{fontSize:12,color:T.muted,textAlign:"center"}}>{d.headcount}</div>
            <div style={{fontFamily:"JetBrains Mono,monospace",fontSize:13,fontWeight:800,color:T.cyan}}>{fmtINR(d.total_cost)}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SALARY TAB
// ══════════════════════════════════════════════════════════════════
function SalaryTab({flash}) {
  const {T} = useTheme();
  const [data,setData]       = useState([]);
  const [loading,setLoading] = useState(true);
  const today = new Date();
  const from  = `${today.getFullYear()-1}-${String(today.getMonth()+2).padStart(2,"0")}`;
  const to    = today.toISOString().slice(0,7);
  useEffect(()=>{
    setLoading(true);
    axios.get(`${API}/expenses/salary-sync?from_month=${from}&to_month=${to}`)
      .then(r=>setData(r.data||[])).catch(()=>flash("❌ Failed"))
      .finally(()=>setLoading(false));
  },[]);
  const maxGross=Math.max(...data.map(d=>d.gross),1);
  return (
    <div>
      <div style={{marginBottom:14,padding:"12px 16px",borderRadius:10,
        background:`${T.green}08`,border:`1px solid ${T.green}20`,
        fontSize:13,color:T.muted,display:"flex",alignItems:"center",gap:8}}>
        <Users size={14} style={{color:T.green}}/>
        Live from processed payroll. Process payroll first to see data here.
      </div>
      {loading?<div style={{padding:40,textAlign:"center",color:T.muted}}>Loading…</div>
      :data.length===0?<Card style={{padding:"48px 24px",textAlign:"center"}}>
        <div style={{fontSize:14,color:T.dim}}>No payroll data found</div>
      </Card>:(
        <Card>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Month","Head","Gross","Net","PF","ESI","TDS","Cost/Head",""].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,
                    color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r,i)=>(
                <tr key={r.month} style={{borderBottom:`1px solid ${T.border}30`,
                  background:i%2===0?"transparent":T.card2}}>
                  <td style={{padding:"12px 14px",fontWeight:700,color:T.text}}>{monthLbl(r.month)}</td>
                  <td style={{padding:"12px 14px",color:T.muted}}>{r.headcount}</td>
                  <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",fontWeight:700,color:T.cyan}}>{fmtINR(r.gross)}</td>
                  <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",color:T.green}}>{fmtINR(r.net)}</td>
                  <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",color:T.muted,fontSize:12}}>{fmtINR(r.pf_total)}</td>
                  <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",color:T.muted,fontSize:12}}>{fmtINR(r.esi_total)}</td>
                  <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",color:T.amber,fontSize:12}}>{fmtINR(r.tds_total)}</td>
                  <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",color:T.purple}}>{fmtINR(r.cost_per_head)}</td>
                  <td style={{padding:"12px 14px"}}>
                    <div style={{width:72,height:6,background:T.toggle_bg,borderRadius:3}}>
                      <div style={{height:"100%",borderRadius:3,background:T.green,
                        width:`${Math.round(r.gross/maxGross*100)}%`}}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// REPORTS TAB
// ══════════════════════════════════════════════════════════════════
function ReportsTab({flash}) {
  const {T,theme} = useTheme();
  const inp = useInp();
  const today = new Date();
  const [from,setFrom]      = useState(`${today.getFullYear()}-01`);
  const [to,setTo]          = useState(today.toISOString().slice(0,7));
  const [data,setData]      = useState([]);
  const [loading,setLoading]= useState(false);

  const generate=async()=>{
    setLoading(true);
    try{const r=await axios.get(`${API}/expenses/analytics?months=12`);
      const all=r.data.trend||[];
      setData(all.filter(t=>t.month>=from&&t.month<=to));}
    catch{flash("❌ Error");}
    finally{setLoading(false);}
  };
  const exportCSV=async()=>{
    try{const r=await axios.get(`${API}/expenses/export-csv?month=${from}`,{responseType:"blob"});
      const url=URL.createObjectURL(new Blob([r.data]));
      const a=document.createElement("a");a.href=url;a.download="Expenses_Report.csv";a.click();
      URL.revokeObjectURL(url);}
    catch{flash("❌ Export failed");}
  };
  return (
    <div>
      <Card style={{padding:"20px 24px",marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:14}}>Generate Report</div>
        <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase"}}>From Month</label>
            <input type="month" value={from} onChange={e=>setFrom(e.target.value)} style={{...inp,width:160,colorScheme:theme}}/>
          </div>
          <div>
            <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase"}}>To Month</label>
            <input type="month" value={to} min={from} onChange={e=>setTo(e.target.value)} style={{...inp,width:160,colorScheme:theme}}/>
          </div>
          <Btn onClick={generate} disabled={loading}>{loading?"Generating…":<><BarChart2 size={13}/>Generate</>}</Btn>
          <Btn onClick={exportCSV} variant="ghost"><Download size={13}/>Export CSV</Btn>
        </div>
      </Card>
      {data.length>0&&(
        <Card>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                {["Month","Vendor","Salary","Total","MoM"].map(h=>(
                  <th key={h} style={{padding:"10px 16px",textAlign:"left",fontSize:10,
                    color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r,i)=>(
                <tr key={r.month} style={{borderBottom:`1px solid ${T.border}30`,
                  background:i%2===0?"transparent":T.card2}}>
                  <td style={{padding:"12px 16px",fontWeight:700,color:T.text}}>{monthLbl(r.month)}</td>
                  <td style={{padding:"12px 16px",color:T.purple,fontFamily:"JetBrains Mono,monospace",fontWeight:700}}>{fmtINR(r.vendor)}</td>
                  <td style={{padding:"12px 16px",color:T.green,fontFamily:"JetBrains Mono,monospace",fontWeight:700}}>{fmtINR(r.salary)}</td>
                  <td style={{padding:"12px 16px",color:T.cyan,fontFamily:"JetBrains Mono,monospace",fontWeight:800,fontSize:14}}>{fmtINR(r.grand)}</td>
                  <td style={{padding:"12px 16px"}}>
                    {r.mom_pct!=null?(
                      <span style={{fontSize:12,fontWeight:700,
                        color:r.mom_pct<=0?T.green:T.red,
                        display:"flex",alignItems:"center",gap:4}}>
                        {r.mom_pct<=0?<TrendingDown size={11}/>:<TrendingUp size={11}/>}{Math.abs(r.mom_pct)}%
                      </span>
                    ):<span style={{color:T.dim}}>—</span>}
                  </td>
                </tr>
              ))}
              <tr style={{borderTop:`1px solid ${T.border}`,background:T.card2}}>
                <td style={{padding:"12px 16px",fontWeight:700,color:T.cyan}}>TOTAL</td>
                <td style={{padding:"12px 16px",color:T.purple,fontFamily:"JetBrains Mono,monospace",fontWeight:800}}>{fmtINR(data.reduce((a,r)=>a+r.vendor,0))}</td>
                <td style={{padding:"12px 16px",color:T.green,fontFamily:"JetBrains Mono,monospace",fontWeight:800}}>{fmtINR(data.reduce((a,r)=>a+r.salary,0))}</td>
                <td style={{padding:"12px 16px",color:T.cyan,fontFamily:"JetBrains Mono,monospace",fontWeight:900,fontSize:15}}>{fmtINR(data.reduce((a,r)=>a+r.grand,0))}</td>
                <td/>
              </tr>
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN MODULE
// ══════════════════════════════════════════════════════════════════
function ExpenseTracker() {
  const {T} = useTheme();
  const inp = useInp();
  const [tab,setTab]         = useState("dashboard");
  const [month,setMonth]     = useState(curMonth());
  const [summary,setSummary] = useState(null);
  const [loading,setLoading] = useState(true);
  const [toast,setToast]     = useState("");

  const flash = msg => { setToast(msg); setTimeout(()=>setToast(""),4000); };

  const loadSummary = useCallback(async()=>{
    setLoading(true);
    try{ const r=await axios.get(`${API}/expenses/summary?month=${month}`); setSummary(r.data); }
    catch{ flash("❌ Failed to load"); }
    finally{ setLoading(false); }
  },[month]);
  useEffect(()=>{ loadSummary(); },[loadSummary]);

  const TABS = [
    {key:"dashboard",      label:"Dashboard",        icon:BarChart2  },
    {key:"salary",         label:"Salary Cost",      icon:Users      },
    {key:"vendors",        label:"Vendors",          icon:Package    },
    {key:"reimbursements", label:"Reimbursements",   icon:Receipt    },
    {key:"analytics",      label:"Analytics",        icon:TrendingUp },
    {key:"budget",         label:"Budget",           icon:Target     },
    {key:"department",     label:"Departments",      icon:Building2  },
    {key:"reports",        label:"Reports",          icon:FileText   },
  ];

  return (
    <div style={{fontFamily:"Outfit,sans-serif",color:T.text,
      background:T.bg,minHeight:"100%",padding:"0 0 40px",
      transition:"background 0.25s,color 0.25s"}}>
      <div style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:11,color:T.cyan,fontWeight:700,
            letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>FINANCE</div>
          <h2 style={{fontSize:22,fontWeight:800,margin:"0 0 4px",color:T.text}}>
            Expense Tracker
          </h2>
          <p style={{fontSize:13,color:T.muted,margin:0}}>
            Vendor costs · SaaS · Infrastructure · Salary · Budgets · Analytics
          </p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="month" value={month} onChange={e=>setMonth(e.target.value)}
            style={{...inp,width:160}}/>
          <Btn onClick={loadSummary} variant="ghost" size="sm">
            <RefreshCw size={12}/>
          </Btn>
        </div>
      </div>
      <div style={{display:"flex",gap:2,borderBottom:`1px solid ${T.border}`,
        marginBottom:20,overflowX:"auto"}}>
        {TABS.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)} style={{
            padding:"10px 16px",borderRadius:"8px 8px 0 0",border:"none",
            flexShrink:0,cursor:"pointer",fontFamily:"Outfit,sans-serif",
            fontSize:12,fontWeight:700,marginBottom:tab===t.key?-1:0,
            background:tab===t.key?T.card:"transparent",
            color:tab===t.key?T.cyan:T.muted,
            borderTop:tab===t.key?`1px solid ${T.border}`:"none",
            borderLeft:tab===t.key?`1px solid ${T.border}`:"none",
            borderRight:tab===t.key?`1px solid ${T.border}`:"none",
            display:"flex",alignItems:"center",gap:6,
          }}>
            {React.createElement(t.icon,{size:13})}{t.label}
          </button>
        ))}
      </div>
      {tab==="dashboard"      && <DashboardTab      month={month} summary={summary} loading={loading}/>}
      {tab==="salary"         && <SalaryTab         flash={flash}/>}
      {tab==="vendors"        && <VendorsTab        month={month} onRefresh={loadSummary} flash={flash}/>}
      {tab==="reimbursements" && <ReimbursementsTab flash={flash}/>}
      {tab==="analytics"      && <AnalyticsTab      flash={flash}/>}
      {tab==="budget"         && <BudgetTab         month={month} flash={flash}/>}
      {tab==="department"     && <DepartmentTab     month={month} flash={flash}/>}
      {tab==="reports"        && <ReportsTab        flash={flash}/>}
      <Toast msg={toast}/>
    </div>
  );
}

export default ExpenseTracker;